import { Suggestie, useCompleteSuggestie, useHerstelSuggestie, useWeigerSuggestie } from "@/entities/suggestie";
import { Button, dismissToast, TimelineCard, toastWithUndo } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { useState } from "react";
import { Link } from "react-router";

type SuggestieTijdlijnCardProps = {
    dossierId: string;
    suggestie: Suggestie;
};

export function SuggestieTijdlijnCard({ suggestie, dossierId }: Readonly<SuggestieTijdlijnCardProps>) {
    const [error, setError] = useState<Error | null>(null);
    const [restoreError, setRestoreError] = useState<Error | null>(null);
    const weigerSuggestieMutation = useWeigerSuggestie();
    const completeSuggestieMutation = useCompleteSuggestie();
    const herstelSuggestieMutation = useHerstelSuggestie();

    const handleReject = async () => {
        setError(null);
        try {
            // Execute rejection immediately
            await weigerSuggestieMutation.mutateAsync({
                dossierId,
                suggestieId: suggestie.id,
            });

            // Show toast with undo functionality
            toastWithUndo("Suggestie geweigerd", "De suggestie is geweigerd", (id: string | number) => {
                herstelSuggestieMutation
                    .mutateAsync({
                        dossierId,
                        suggestieId: suggestie.id,
                    })
                    .catch((error) => {
                        setRestoreError(error as Error);
                    })
                    .finally(() => {
                        dismissToast(id);
                    });
            });
        } catch (error) {
            setError(error as Error);
        }
    };

    const handleComplete = async () => {
        setError(null);
        try {
            // Execute rejection immediately
            await completeSuggestieMutation.mutateAsync({
                dossierId,
                suggestieId: suggestie.id,
            });

            // Show toast with undo functionality
            toastWithUndo("Suggestie voldaan", "De suggestie is voldaan", (id: string | number) => {
                herstelSuggestieMutation
                    .mutateAsync({
                        dossierId,
                        suggestieId: suggestie.id,
                    })
                    .catch((error) => {
                        setRestoreError(error as Error);
                    })
                    .finally(() => {
                        dismissToast(id);
                    });
            });
        } catch (error) {
            setError(error as Error);
        }
    };

    if (suggestie.variant === "Message") {
        return <MessageSuggestie suggestie={suggestie} error={error} restoreError={restoreError} handler={handleComplete} />;
    }
    return <ActieSuggestie suggestie={suggestie} error={error} restoreError={restoreError} handler={handleReject} />;
}

type SuggestieTimelineCardProps = {
    suggestie: Suggestie;
    error: Error | null;
    restoreError: Error | null;
    handler: () => Promise<void>;
};

function ActieSuggestie({ suggestie, error, restoreError, handler }: SuggestieTimelineCardProps) {
    return (
        <TimelineCard className="flex flex-col gap-3" data-testid={`suggestie-list-item-${suggestie.type}`}>
            {error && <ErrorDisplay error={error} title="Kan suggestie niet weigeren" />}
            {restoreError && <ErrorDisplay error={restoreError} title="Kan suggestie niet herstellen" />}
            <div className="flex flex-col">
                <span className="text-m leading-l text-content-default">{suggestie.titel}</span>
                <span className="text-s leading-l text-content-subtle">{suggestie.omschrijving}</span>
            </div>
            <div className="flex flex-row justify-between gap-2">
                <Link to={suggestie.navigeerNaar}>
                    <Button className="self-start" variant="secondary" size="s" icon={suggestie.knopIcon} iconSize="xs">
                        {suggestie.knopTekst}
                    </Button>
                </Link>
                <Button className="self-start" variant="tertiary" iconOnly size="s" icon="close" onClick={handler} />
            </div>
        </TimelineCard>
    );
}

function MessageSuggestie({ suggestie, error, restoreError, handler }: SuggestieTimelineCardProps) {
    return (
        <TimelineCard className="flex flex-col gap-3" data-testid={`suggestie-list-item-${suggestie.type}`}>
            {error && <ErrorDisplay error={error} title="Kan suggestie niet voldoen" />}
            {restoreError && <ErrorDisplay error={restoreError} title="Kan suggestie niet herstellen" />}
            <div className="flex flex-row items-center justify-between">
                <div className="flex flex-col">
                    <span className="text-m leading-l text-content-default">{suggestie.titel}</span>
                    <span className="text-s leading-l text-content-subtle">{suggestie.omschrijving}</span>
                </div>
                <div className="flex flex-row justify-between gap-2">
                    <Button className="self-start" variant="tertiary" iconOnly size="s" icon="check" onClick={handler} />
                </div>
            </div>
        </TimelineCard>
    );
}
